import React from 'react'
import { Button, Modal } from 'react-bootstrap'
import {
  isMobile,
  numberWithCommas,
  calculateCommissionValue,
  decomposeStandaloneVipAmount,
  getStandaloneVipPriceBreakdown,
  formatChargesText,
} from 'helpers'
import { parseChargePercent } from 'helpers/promoTerms'
const isMobileBrowser = isMobile()

const parseAmount = val => {
  if (val === undefined || val === null || val === '') return 0
  if (typeof val === 'number') return val
  return parseFloat(String(val).replace(/,/g, '')) || 0
}

const getDiscountWeight = discountWeight => {
  if (
    discountWeight &&
    typeof discountWeight === 'object' &&
    discountWeight.weight
  ) {
    return discountWeight.weight
  }
  return discountWeight || null
}

const PriceItem = ({ label, value, currency, weight, isNegative, bold }) => {
  const amount = (
    <>
      {isNegative ? '-' : ''}
      {numberWithCommas(parseAmount(value).toFixed(2))} {currency}
    </>
  )
  return (
    <div className='price-item'>
      <span className='label'>
        {label}
        {weight != null && weight > 0 ? ` (${weight}%)` : ''}
      </span>
      <span className='dots'></span>
      <span className='value'>{bold ? <b>{amount}</b> : amount}</span>
    </div>
  )
}

export const PriceBreakdownModal = ({
  show,
  onHide,
  priceBreakdown,
  isB2C = false,
  hasBenefits = false,
  charges = [],
  arrival = null,
  departure = null,
  actualRate = null,
  booking = null,
  type = null,
  pickUpLocation = null,
}) => {
  const {
    commissionValue,
    commissionWeight,
    discountValue,
    discountWeight,
    referralDiscountAmount,
    rolzoCreditsRedeemed,
    priveDiscountApplied,
    currency,
    clientMarkupRate: priceBreakdownClientMarkupRate,
    clientMarkup,
    markupWeight: priceBreakdownMarkupWeight,
    fullBooking,
    rate,
    salesTaxAmount,
    salesTaxRate,
    waitCoverage,
    cancelCoverage,
    rolzoCreditsUsed,
    vipAdditionalHours,
    vipAdditionalHoursAmount,
    vipAdditionalHoursDiscount,
    vipAdditionalHoursCommission,
    vipAdditionalHoursMarkup,
    chargeCommissionWeight,
    chargeDiscountWeight,
  } = priceBreakdown
  // A charge earns its OWN stored rate first, else the fallback — the same
  // order as the client payout (insertClientChargesPayout). The fallback is the
  // booking-level commission unless the caller passes chargeCommissionWeight.
  const chargeFallbackCommissionWeight =
    chargeCommissionWeight !== undefined && chargeCommissionWeight !== null
      ? chargeCommissionWeight
      : commissionWeight
  // Discount weight that reduces a charge's commission base. A promo discount
  // applies to the main service only; the client payout computes a charge's
  // commission on its own base. Defaults to the booking-level discount weight
  // (previous behaviour) unless the caller passes chargeDiscountWeight.
  const getChargeDiscountWeightForCommission = () =>
    chargeDiscountWeight !== undefined && chargeDiscountWeight !== null
      ? chargeDiscountWeight
      : discountWeightValue
  const getChargeItemCommissionWeight = item => {
    const storedWeight = parseChargePercent(item.commission)
    return storedWeight > 0 ? storedWeight : chargeFallbackCommissionWeight
  }
  const waitCovAsNormalRefund =
    parseFloat(
      booking?.refund?.refundHistory?.find(
        entry => entry.reason === 'Extended wait-time was removed.'
      )?.amount
    ) || 0
  const cancelCovAsNormalRefund =
    parseFloat(
      booking?.refund?.refundHistory?.find(
        entry => entry.reason === '1-hour risk-free cancellation was removed.'
      )?.amount
    ) || 0
  const waitChargeItem =
    charges?.some(c =>
      c.items?.some(item => item.description === 'Extended wait-time')
    ) ?? false
  const cancelChargeItem =
    charges?.some(c =>
      c.items?.some(
        item => item.description === '1-hour risk-free cancellation'
      )
    ) ?? false

  const isUKbooking =
    (pickUpLocation?.country === 'GB' ||
      booking?.pickUpLocation?.country === 'GB') &&
    (!booking || new Date(booking?.createdAt) > new Date('2026-08-01'))

  const clientMarkupRate =
    priceBreakdownClientMarkupRate ??
    fullBooking?.clientMarkupRate ??
    booking?.clientMarkupRate

  const discountWeightValue =
    discountWeight &&
    typeof discountWeight === 'object' &&
    discountWeight.weight
      ? discountWeight.weight
      : discountWeight || 0

  let calculatedChargesCommission = 0
  let calculatedChargesMarkup = 0
  let calculatedChargesDiscount = 0
  let vipCommission = 0
  let vipMarkup = 0

  const isStandaloneVip = booking && booking.isStandaloneVip === true

  // STANDALONE VIP — single source of truth. The booking object carries the
  // LIVE post-CR state (actualRate / benefits / clientMarkupRate updated on
  // every CR confirm), so the breakdown is READ from it instead of being
  // re-derived from the frozen `rate` prop (confirmTimeRateInfo.price) mixed
  // with live discount values — that mix produced internally inconsistent
  // numbers (e.g. base 161.25 / discount 33.75 / total 127.50 instead of
  // 180 / 27 / 153). Covers markup, discount, commission, all four promo
  // types, separate non-CR charges, charge-level refunds and manual refunds.
  // CR charges/refunds are NOT re-added: they're already inside actualRate.
  // When set, this overrides the derived aggregates below; the legacy
  // (non-VIP) path is untouched.
  const vipBreakdown = isStandaloneVip
    ? getStandaloneVipPriceBreakdown(booking)
    : null

  // Standalone VIP — CR-derived charges (booking.charges[].items[].isChangeRequest)
  // are summed separately under calculatedChargesCommission below. The total
  // bookingTimeRateInfo.actualRate ALREADY includes those CR deltas (because
  // the CR update at booking.js:27751 folds every delta into actualRate). So
  // when computing baseCommissionValue from actualRate, we must SUBTRACT the
  // CR-charges to avoid double-counting them once in the service base and once
  // in the per-charge tally. Example (booking 7300-R): actualRate 130 includes
  // a +10 CR2 that's in charges; service base = 130 − 10 = 120 → commission
  // base = 15% × 120 = 18, charges add 16.5 → total 34.5 (not the wrong 36
  // that arose from 15% × 130 + 16.5). Refund-discriminator left in place too.
  // Subtract each CR charge's GROSS-BEFORE-MARKUP base (baseAmount), not its full
  // markup-inclusive amount. actualRate is gross-before-markup and only grows by the
  // CR's baseAmount, so subtracting `amount` over-subtracts by the CR's markup and
  // understates the service-row commission base. Fall back to `amount` for legacy
  // markup-less items where baseAmount === amount.
  const _vipCrChargesTotal =
    isStandaloneVip && Array.isArray(booking.charges)
      ? booking.charges
          .filter(c => c && c.isClient)
          .reduce(
            (sum, c) =>
              sum +
              (c.items || [])
                .filter(i => i && i.isChangeRequest)
                .reduce(
                  (s, i) =>
                    s +
                    (i && i.baseAmount != null
                      ? parseFloat(i.baseAmount) || 0
                      : parseFloat(i.amount) || 0),
                  0
                ),
            0
          )
      : 0

  // For standalone VIP: commission/discount are on the NET service amount after booking-level
  // refunds. max(0, ...) guards against edge cases where refund exceeds the service rate.
  const bookingRefundAmount =
    isStandaloneVip && booking.refund && booking.refund.amount
      ? parseFloat(booking.refund.amount) || 0
      : 0
  const vipNetServiceRate = isStandaloneVip
    ? Math.max(
        0,
        (parseFloat(actualRate) || 0) - _vipCrChargesTotal - bookingRefundAmount
      )
    : null

  let baseCommissionValue = 0
  // Non-VIP: prefer confirm-time actualRate when sell price was raised after
  // booking (prop/top-level actualRate often stays on bookingTimeRateInfo).
  const nonVipCommissionActualRate =
    booking?.confirmTimeRateInfo?.actualRate ?? actualRate
  if (
    (isStandaloneVip ? actualRate : nonVipCommissionActualRate) &&
    commissionWeight > 0
  ) {
    // Subtract CR-charges for standalonevip so the service-row commission base
    // matches what FinanceTabV2 displays (and what the customer actually pays
    // on the main intent — separate charges are billed/accounted independently).
    const _vipCommissionBase = isStandaloneVip
      ? Math.max(0, (parseFloat(actualRate) || 0) - _vipCrChargesTotal)
      : nonVipCommissionActualRate
    baseCommissionValue = calculateCommissionValue(
      _vipCommissionBase,
      commissionWeight,
      discountWeightValue,
      (pickUpLocation || booking?.pickUpLocation)?.country
    )
  } else if (commissionValue) {
    if (typeof commissionValue === 'string') {
      baseCommissionValue = parseFloat(commissionValue.replace(/,/g, '')) || 0
    } else {
      baseCommissionValue = parseFloat(commissionValue) || 0
    }
    // if (isUKbooking) {
    //   baseCommissionValue = baseCommissionValue / 1.2
    // }
  }

  if (charges && charges.length > 0) {
    charges.forEach(charge => {
      // Standalone VIP only — refund-aware aggregation:
      //   When a charge of `amount` (markup baked in) is partially refunded by
      //   `refund`, treat the remaining (amount − refund) as a new effective
      //   charge that preserves the original markup percentage. Concretely:
      //   ratio = (amount − refund) / amount, applied uniformly to commission
      //   and markup contributions (and discount, for the discount-only case).
      //   This matches the business rule: refunding 20 from a 200+10%markup=220
      //   charge leaves an effective 200 charge with new_base 181.82, new_markup
      //   18.18, new_commission 15%×181.82 = 27.27.
      // Non-VIP charges keep the original refund-blind behaviour (ratio = 1).
      const chargeRefundAmt =
        isStandaloneVip && charge.refund && charge.refund.amount
          ? parseFloat(charge.refund.amount) || 0
          : 0
      const chargeAmountTotal = (charge.items || []).reduce(
        (s, i) => s + (parseFloat(i.amount) || 0),
        0
      )
      const chargeNetRatio =
        isStandaloneVip && chargeRefundAmt > 0 && chargeAmountTotal > 0
          ? Math.max(
              0,
              (chargeAmountTotal - chargeRefundAmt) / chargeAmountTotal
            )
          : 1

      if (charge.items && charge.items.length > 0) {
        charge.items.forEach(item => {
          if (item.applyCommission) {
            const amountForCommission =
              item.baseAmount !== undefined && item.baseAmount !== null
                ? item.baseAmount
                : item.amount !== undefined && item.amount !== null
                ? item.amount
                : (parseFloat(item.rate) || 0) *
                  (parseFloat(item.quantity) || 1)
            // Commission scaled by the post-refund ratio (standalone VIP only;
            // ratio = 1 otherwise).
            calculatedChargesCommission +=
              calculateCommissionValue(
                amountForCommission,
                getChargeItemCommissionWeight(item),
                getChargeDiscountWeightForCommission(),
                (pickUpLocation || booking.pickUpLocation)?.country
              ) * chargeNetRatio
          }

          if (item.baseAmount !== undefined && item.baseAmount !== null) {
            const itemAmount = parseFloat(item.amount) || 0
            const itemBaseAmount = parseFloat(item.baseAmount) || 0
            // Markup scaled by the same post-refund ratio so total markup
            // matches the implied new_base × markup% on the remaining amount.
            calculatedChargesMarkup +=
              (itemAmount - itemBaseAmount) * chargeNetRatio
          }

          if (item.applyDiscount && item.discount) {
            const pct = parseFloat(String(item.discount).replace('%', '')) || 0
            if (pct > 0) {
              // For standalone VIP additional charges, item.amount is the NET
              // (post-discount) line total stored on the charge — item.rate is
              // the GROSS (pre-discount) rate. To compute the displayed discount
              // the SAME way FinanceTabV2 does (line ~1423), gross up from the
              // NET amount, then take the delta:
              //   gross = net / (1 − pct/100)
              //   itemDiscount = gross − net
              // (Using item.rate here would gross up an already-gross number and
              // overstate the discount, e.g. 100/0.85−100 = 17.65 instead of the
              // correct 85/0.85−85 = 15.00.)
              // For non-VIP charges where rate is gross, fall back to the
              // straight net × pct/100 formula.
              const unitRate = parseFloat(item.rate ?? item.amount) || 0
              const qty = parseFloat(item.quantity) || 1
              const netTotal = unitRate * qty
              let itemDiscount
              if (isStandaloneVip && pct < 100) {
                const netAmount = parseFloat(item.amount) || 0
                const grossTotal = netAmount / (1 - pct / 100)
                itemDiscount = grossTotal - netAmount
              } else {
                itemDiscount = (netTotal * pct) / 100
              }
              // Discount also scaled by the post-refund ratio (markup XOR
              // discount, so this only fires when no markup is on the item).
              calculatedChargesDiscount += itemDiscount * chargeNetRatio
            }
          }
        })
      }
    })
  }

  // Extend the booking-level discountValue (covers the main rate + any VIP
  // add-on) with the per-charge discounts so the modal total matches the
  // Total row shown to the client (main + additional charges).
  let baseDiscountValueNum = 0
  if (isStandaloneVip && vipNetServiceRate !== null && discountWeight > 0) {
    // For standalone VIP: recalculate discount on the net service rate, same reason as commission.
    baseDiscountValueNum =
      (vipNetServiceRate * (parseFloat(discountWeight) || 0)) / 100
  } else if (discountValue !== undefined && discountValue !== null) {
    if (typeof discountValue === 'string') {
      baseDiscountValueNum = parseFloat(discountValue.replace(/,/g, '')) || 0
    } else {
      baseDiscountValueNum = parseFloat(discountValue) || 0
    }
  }
  const totalDiscountValue = vipBreakdown
    ? vipBreakdown.discountValue
    : baseDiscountValueNum + calculatedChargesDiscount

  if (arrival && arrival.rate) {
    const arrivalOriginalRate =
      arrival.baseRate !== undefined && arrival.baseRate !== null
        ? arrival.baseRate
        : arrival.rate - (arrival.clientMarkupRate || 0)
    vipCommission += calculateCommissionValue(
      arrivalOriginalRate,
      commissionWeight,
      discountWeightValue,
      (pickUpLocation || booking.pickUpLocation)?.country
    )
    vipMarkup += parseFloat(arrival.clientMarkupRate || 0)
  }

  if (departure && departure.rate) {
    const departureOriginalRate =
      departure.baseRate !== undefined && departure.baseRate !== null
        ? departure.baseRate
        : departure.rate - (departure.clientMarkupRate || 0)
    vipCommission += calculateCommissionValue(
      departureOriginalRate,
      commissionWeight,
      discountWeightValue,
      (pickUpLocation || booking.pickUpLocation)?.country
    )
    vipMarkup += parseFloat(departure.clientMarkupRate || 0)
  }

  const totalCommissionValue = vipBreakdown
    ? vipBreakdown.commissionValue
    : baseCommissionValue + calculatedChargesCommission + vipCommission
  // Standalone VIP: clientMarkupRate is the LIVE TOTAL markup (recalculated on each
  // change request to include the CR's markup). The base service markup is derived by
  // ONE universal formula that is correct for PAID bookings (CR billed on a separate
  // charge row) AND for UNPAID-folded bookings (CR folded into the live rate, no charge
  // row): baseMarkup = clientMarkupRate − Σ(CR charge markups) + Σ(CR refund markups).
  // CR charge markup that is also tallied in calculatedChargesMarkup is removed here, and
  // CR refund markup subtracted via _vipRefundMarkup is added back, so the grand total
  // nets to clientMarkupRate. The old confirm-time snapshot silently dropped the folded
  // CR markup on unpaid bookings. Non-VIP keeps clientMarkupRate.
  let _baseServiceMarkup = parseFloat(clientMarkupRate) || 0
  if (isStandaloneVip) {
    const _cmForBase = booking.clientMarkup
    let _crChargeMarkup = 0
    if (Array.isArray(booking.charges)) {
      booking.charges
        .filter(c => c.isClient)
        .forEach(c => {
          if (Array.isArray(c.items)) {
            c.items
              .filter(i => i.isChangeRequest)
              .forEach(i => {
                const gross = parseFloat(i.amount) || 0
                const baseAmt =
                  parseFloat(i.baseAmount != null ? i.baseAmount : i.amount) ||
                  0
                _crChargeMarkup += Math.max(0, gross - baseAmt)
              })
          }
        })
    }
    let _crRefundMarkup = 0
    if (booking.refund && Array.isArray(booking.refund.refundHistory)) {
      booking.refund.refundHistory
        .filter(r => r && r.isChangeRequest)
        .forEach(r => {
          _crRefundMarkup += decomposeStandaloneVipAmount(r.amount, _cmForBase)
            .markup
        })
    }
    _baseServiceMarkup = Math.max(
      0,
      (parseFloat(clientMarkupRate) || 0) - _crChargeMarkup + _crRefundMarkup
    )
  }
  // Standalone VIP: a booking-level refund reverses the CR charge that created it.
  // calculatedChargesMarkup above ALREADY added the +charge's markup; to keep the
  // modal consistent with the finance tab (which shows a −refund markup deduction
  // that cancels the +charge), subtract the refund's markup portion here. The refund
  // is decomposed by the SAME rule as the charge (decomposeStandaloneVipAmount), so
  // for booking 7303-R: base markup 10 + charge markup 5 − refund markup 5 = 10.
  let _vipRefundMarkup = 0
  if (isStandaloneVip && booking.refund) {
    const _cm = booking.clientMarkup
    if (
      booking.refund.refundHistory &&
      booking.refund.refundHistory.length > 1
    ) {
      booking.refund.refundHistory.forEach(r => {
        _vipRefundMarkup += decomposeStandaloneVipAmount(r.amount, _cm).markup
      })
    } else {
      _vipRefundMarkup += decomposeStandaloneVipAmount(
        booking.refund.amount,
        _cm
      ).markup
    }
  }
  const totalMarkupValue = vipBreakdown
    ? vipBreakdown.markupValue
    : Math.max(
        0,
        _baseServiceMarkup +
          calculatedChargesMarkup +
          vipMarkup -
          _vipRefundMarkup
      )

  // Markup config: priceBreakdown prop → add-on VIP leg → standalone booking doc.
  // Add-on VIP stores clientMarkup on arrival/departure, not on the booking root.
  // Never back-calculate % from surged totals — event surge (applySurgeToMG) inflates
  // the denominator and shows ~7% when the configured markup is 10%.
  const _markupConfig =
    clientMarkup ??
    arrival?.clientMarkup ??
    departure?.clientMarkup ??
    (isStandaloneVip ? booking?.clientMarkup : null)
  const markupWeight =
    _markupConfig?.type === 'percentage' && _markupConfig?.value > 0
      ? _markupConfig.value
      : null

  // Exact stored percentage markup weight on the booking (e.g. 10). Prefer this
  // over deriving the weight from amount ÷ base: a transfer's markup amount (40)
  // over its shown base (365, surge-inflated / net of other deltas) rounds to
  // 11%, disagreeing with the true configured 10% that the VIP M&G card shows
  // from its own clientMarkup config. Only used when the per-item clientMarkup
  // percentage config (markupWeight above) is absent — which is the case for the
  // transfer/vehicle card. Guard to a positive number so it never overrides a
  // real config or a fixed-amount markup.
  const _storedMarkupWeight =
    typeof priceBreakdownMarkupWeight === 'number' &&
    priceBreakdownMarkupWeight > 0
      ? priceBreakdownMarkupWeight
      : typeof booking?.markupWeight === 'number' && booking.markupWeight > 0
      ? booking.markupWeight
      : typeof fullBooking?.markupWeight === 'number' &&
        fullBooking.markupWeight > 0
      ? fullBooking.markupWeight
      : null

  const resolvedMarkupWeight = markupWeight ?? _storedMarkupWeight ?? null

  const serviceLabel = type || 'Transfer'
  // Standalone VIP: grand client total (live price incl. all CRs + separate
  // charges − refunds) — matches the Total row the ⓘ icon annotates. The
  // legacy `rate` prop is the FROZEN confirm-time price and excludes CRs.
  // SERVICE-scoped: the separate additional charges render as their own rows
  // below and are added to the grand total there, so including them here too
  // showed the charge inside the service line AND again underneath it, and the
  // grand total counted it twice (a 250 booking with an 80 charge read 410).
  // `vipBreakdown.total` stays the whole-booking figure for "Your earnings".
  const totalRate = vipBreakdown
    ? vipBreakdown.serviceTotal !== undefined
      ? vipBreakdown.serviceTotal
      : vipBreakdown.total
    : parseAmount(
        (booking?.confirmTimeRateInfo?.price ??
          booking?.bookingTimeRateInfo?.price ??
          rate) -
          (waitCoverage && !waitChargeItem ? waitCoverage : 0) -
          (cancelCoverage && !cancelChargeItem ? cancelCoverage : 0)
      )
  const referralNum = parseAmount(referralDiscountAmount)
  const creditsNum = parseAmount(rolzoCreditsRedeemed || rolzoCreditsUsed)
  const waitCoverageNum = parseAmount(waitCoverage)
  const cancelCoverageNum = parseAmount(cancelCoverage)
  const markupAmount =
    vipBreakdown && vipBreakdown.serviceMarkupValue !== undefined
      ? vipBreakdown.serviceMarkupValue
      : totalMarkupValue
  // The service row's OWN discount: each additional charge already shows its
  // discount on its own row, so the charges' share must not be added here too
  // (it inflated the service "Base price" and "Discount" by the charge discount).
  const serviceDiscountValue =
    vipBreakdown && vipBreakdown.serviceDiscountValue !== undefined
      ? vipBreakdown.serviceDiscountValue
      : baseDiscountValueNum
  const baseServiceRate = Math.max(
    0,
    totalRate -
      markupAmount +
      serviceDiscountValue +
      referralNum +
      creditsNum -
      waitCovAsNormalRefund -
      cancelCovAsNormalRefund
  )
  // Purchased extra greeter hours. They are folded into the service line on the
  // Payment Information screen, so this breakdown is where the client sees what
  // they cost — but `totalRate` (the VIP total) ALREADY contains them, and so
  // therefore does baseServiceRate. Listing the hours on their own row without
  // taking them back out of the base would show the same 75 twice.
  // GROSS, not the charged amount: `baseServiceRate` below is pre-discount, so
  // taking the discounted hours out of it leaves the hours' own discount share
  // stranded in the base (5449-V read Base price 6.00 for a real 5.00 base;
  // 9651-R read 742.50 for 735.00). The breakdown exposes the gross it actually
  // counted; the prop is the Step 1 path, where there is no booking to derive from.
  const vipAddlHoursNum =
    vipBreakdown && vipBreakdown.hoursInPriceGross > 0
      ? vipBreakdown.hoursInPriceGross
      : parseAmount(vipAdditionalHoursAmount)
  const vipAddlHoursCount =
    vipBreakdown && vipBreakdown.hoursInPriceHours > 0
      ? vipBreakdown.hoursInPriceHours
      : parseInt(vipAdditionalHours, 10) || 0
  // The hours' OWN share of each money line, shown indented beneath the row the
  // same way an additional charge shows its own. On a saved booking these come
  // from the breakdown; during creation (steps 1-2 and booking creation) there
  // is no booking, so the step's priceBreakdown carries them.
  //
  // Markup is creation-only by nature: once the booking is saved the hours'
  // markup has been merged into the single stored clientMarkupRate and there is
  // no field that still separates it, so the line is simply omitted there
  // rather than shown as a guess.
  const vipAddlHoursDiscountNum = vipBreakdown
    ? parseAmount(vipBreakdown.hoursDiscountValue)
    : parseAmount(vipAdditionalHoursDiscount)
  const vipAddlHoursCommissionNum = vipBreakdown
    ? parseAmount(vipBreakdown.hoursCommissionValue)
    : parseAmount(vipAdditionalHoursCommission)
  const vipAddlHoursMarkupNum = vipBreakdown
    ? 0
    : parseAmount(vipAdditionalHoursMarkup)
  // Also drives this section's UK VAT/net sub-lines: the hours render as their
  // own row via renderCoverageItem, which emits ITS OWN "Includes taxes" /
  // "Net amount" pair, so leaving them inside the service base charged VAT on
  // them twice on a GB booking.
  const baseServiceRateExHours = Math.max(0, baseServiceRate - vipAddlHoursNum)
  // The base row's OWN discount share. serviceDiscountValue is the COMBINED
  // discount (base + hours — e.g. a 500 gross × 20% = 100), and the hours row
  // below already renders its own share (vipAddlHoursDiscountNum, e.g. a 200
  // gross × 20% = 40). Showing the full combined figure here too double-counts
  // that 40: the client would read -100 under a 300 base AND -40 under the
  // hours, an apparent 140 total discount for what is really only 100.
  const baseServiceDiscountExHours = Math.max(
    0,
    serviceDiscountValue - vipAddlHoursDiscountNum
  )
  // Same double-count as the discount line above, for commission: baseCommissionValue
  // is the COMBINED base+hours commission (e.g. 15% × 500 = 75), and the hours row
  // below already renders its own share (vipAddlHoursCommissionNum, e.g. 15% × 200 =
  // 30). Showing the full 75 here too reads as 75+30=105 total commission for what
  // is really only 75 (matches "Total commission earned" in the earnings section,
  // which is unaffected — it still sums from the untouched baseCommissionValue).
  const baseCommissionExHours = Math.max(
    0,
    baseCommissionValue - vipAddlHoursCommissionNum
  )
  // Same double-count, for markup — but ONLY at creation (Step 1/2), where
  // renderExtendedGreeterItem() shows the hours' own markup share
  // (vipAddlHoursMarkupNum). On a SAVED booking vipAddlHoursMarkupNum is hard-
  // coded to 0 above (the hours' markup has already been merged into the one
  // stored clientMarkupRate with no field left to separate it back out), so
  // this is a no-op there and markupAmount is correctly the whole figure.
  // `markupAmount` itself (the combined base+hours figure) must stay
  // untouched: `totalPrice` below and the "Total markup earned" section both
  // need the true combined total, not the base-only share.
  const baseMarkupExHours = Math.max(0, markupAmount - vipAddlHoursMarkupNum)
  const totalPrice =
    (totalRate > 0
      ? totalRate
      : baseServiceRate + markupAmount - serviceDiscountValue) -
    waitCovAsNormalRefund -
    cancelCovAsNormalRefund
  const totalEarnings =
    totalCommissionValue +
    (isUKbooking ? totalMarkupValue / 1.2 : totalMarkupValue)
  const showEarningsSection = totalCommissionValue > 0 || totalMarkupValue > 0
  const discWeight = vipBreakdown
    ? vipBreakdown.discountWeightLabel
    : getDiscountWeight(discountWeight)

  // ── VIP Meet & Greet Transit with purchased greeter hours ────────────────
  // The VIP service and the Extended greeter assistance are shown as two
  // SEPARATE instances, each with its own charged total and its own base,
  // discount, VAT, commission and markup lines, then a Total price that sums
  // them. Every figure below is the hours' own share or the VIP's own share of
  // the booking's combined aggregates — the two always add back up to them.
  const vipHoursMarkupNum =
    vipBreakdown && vipBreakdown.hoursMarkupValue !== undefined
      ? parseAmount(vipBreakdown.hoursMarkupValue)
      : vipAddlHoursMarkupNum
  const vipHoursTotal = Math.max(
    0,
    vipAddlHoursNum - vipAddlHoursDiscountNum + vipHoursMarkupNum
  )
  const vipOwnTotal = Math.max(0, totalPrice - vipHoursTotal)
  const vipOwnMarkup = Math.max(0, markupAmount - vipHoursMarkupNum)
  const vipOwnDiscount = Math.max(
    0,
    serviceDiscountValue - vipAddlHoursDiscountNum
  )
  const vipOwnBase = Math.max(0, vipOwnTotal - vipOwnMarkup + vipOwnDiscount)
  // The service's commission (VIP + hours, separate charges excluded). On a
  // saved booking it is read from the breakdown, never re-derived from the
  // actualRate prop — actualRate is stored hours-inclusive on some bookings and
  // hours-exclusive on others, so subtracting the hours' share from it removed
  // the hours' commission twice on the latter.
  const vipServiceCommission =
    vipBreakdown && vipBreakdown.serviceCommissionValue !== undefined
      ? parseAmount(vipBreakdown.serviceCommissionValue)
      : baseCommissionValue
  const vipOwnCommission = Math.max(
    0,
    vipServiceCommission - vipAddlHoursCommissionNum
  )
  const vipCommissionWeight = vipBreakdown
    ? vipBreakdown.commissionWeightLabel
    : commissionWeight
  const isVipHoursLayout = vipAddlHoursNum > 0 && !arrival && !departure

  const onClose = () => {
    onHide()
  }

  const hasCharges =
    Array.isArray(charges) &&
    charges.some(charge => charge.items && charge.items.length > 0)

  const chargesTotal = hasCharges
    ? charges.reduce(
        (sum, charge) =>
          sum +
          (charge.items || []).reduce(
            (s, i) => s + (parseFloat(i.amount) || 0),
            0
          ) -
          (charge.refund ? parseFloat(charge.refund.amount) || 0 : 0),
        0
      )
    : 0

  const arrivalTotal = parseAmount(arrival?.rate)
  const departureTotal = parseAmount(departure?.rate)

  const hasAdditionalSections =
    hasCharges ||
    waitCoverageNum > 0 ||
    cancelCoverageNum > 0 ||
    referralNum > 0 ||
    creditsNum > 0 ||
    arrivalTotal > 0 ||
    departureTotal > 0

  const grandTotalPrice =
    totalPrice +
    arrivalTotal +
    departureTotal +
    chargesTotal +
    (waitChargeItem ? 0 : waitCoverageNum) +
    (cancelChargeItem ? 0 : cancelCoverageNum) -
    referralNum -
    creditsNum

  // A booking a discount made free still shows its total of 0
  const isFreeByDiscount = totalDiscountValue > 0 && grandTotalPrice < 0.005

  const renderTotalPriceLine = () =>
    (hasAdditionalSections || isFreeByDiscount) && (
      <>
        <hr />
        <PriceItem
          label={<b>Total price</b>}
          value={grandTotalPrice}
          currency={currency}
        />
      </>
    )

  const renderChargeItem = (item, charge, key, isLastItem) => {
    const itemAmount = parseFloat(item.amount) || 0
    const itemBaseAmount =
      item.baseAmount !== undefined && item.baseAmount !== null
        ? parseFloat(item.baseAmount) || 0
        : null
    const itemMarkup =
      itemBaseAmount !== null ? Math.max(0, itemAmount - itemBaseAmount) : 0
    const netBeforeMarkup = itemAmount - itemMarkup
    // A charge's markup % is the item's own (admin-editable, stored on the item
    // as a number), else the booking's percentage config. A fixed markup has
    // no percentage to show.
    const itemMarkupConfigValue = parseFloat(item.clientMarkup)
    const itemMarkupWeight =
      _markupConfig?.type === 'percentage'
        ? itemMarkupConfigValue > 0
          ? itemMarkupConfigValue
          : markupWeight
        : null

    const itemCommissionWeight = getChargeItemCommissionWeight(item)
    const itemCommission = item.applyCommission
      ? calculateCommissionValue(
          itemBaseAmount !== null
            ? itemBaseAmount
            : item.amount !== undefined && item.amount !== null
            ? parseFloat(item.amount) || 0
            : (parseFloat(item.rate) || 0) * (parseFloat(item.quantity) || 1),
          itemCommissionWeight,
          getChargeDiscountWeightForCommission(),
          (pickUpLocation || booking?.pickUpLocation)?.country
        )
      : 0

    const itemDiscountPct =
      item.applyDiscount && item.discount
        ? parseFloat(String(item.discount).replace('%', '')) || 0
        : 0
    const unitRate = parseFloat(item.rate ?? item.amount) || 0
    const qty = parseFloat(item.quantity) || 1
    const itemDiscount =
      itemDiscountPct > 0 ? (unitRate * qty * itemDiscountPct) / 100 : 0

    const label = isStandaloneVip
      ? item.description
      : formatChargesText(item.description, !isMobileBrowser, charge.type)

    return (
      <React.Fragment key={key}>
        <PriceItem label={label} value={itemAmount} currency={currency} />
        <div style={{ paddingLeft: '16px' }}>
          {itemBaseAmount !== null && itemBaseAmount !== itemAmount && (
            <PriceItem
              label='Base price'
              value={itemBaseAmount}
              currency={currency}
            />
          )}
          {itemDiscount > 0 && (
            <PriceItem
              label='Discount'
              value={itemDiscount}
              currency={currency}
              weight={itemDiscountPct}
              isNegative
            />
          )}
          {isUKbooking && netBeforeMarkup > 0 && (
            <>
              <PriceItem
                label='Includes taxes (VAT 20%)'
                value={netBeforeMarkup * (20 / 120)}
                currency={currency}
              />
              <PriceItem
                label='Net amount'
                value={netBeforeMarkup / 1.2}
                currency={currency}
              />
            </>
          )}
          {itemCommission > 0 && (
            <PriceItem
              label='Your commission'
              value={itemCommission}
              currency={currency}
              weight={itemCommissionWeight > 0 ? itemCommissionWeight : null}
            />
          )}
          {itemMarkup > 0 && (
            <>
              <PriceItem
                label='Price markup'
                value={itemMarkup}
                currency={currency}
                weight={itemMarkupWeight}
              />
              {isUKbooking && (
                <>
                  <PriceItem
                    label='Includes taxes (VAT 20%)'
                    value={itemMarkup * (20 / 120)}
                    currency={currency}
                  />
                  <PriceItem
                    label='Net markup'
                    value={itemMarkup / 1.2}
                    currency={currency}
                  />
                </>
              )}
            </>
          )}
        </div>
        {isLastItem && charge.refund && (
          <PriceItem
            label='Refund'
            value={parseFloat(charge.refund.amount) || 0}
            currency={currency}
            isNegative
          />
        )}
      </React.Fragment>
    )
  }

  const renderCoverageItem = (label, value) => (
    <>
      <hr />
      <PriceItem label={label} value={value} currency={currency} />
      <div style={{ paddingLeft: '16px' }}>
        <PriceItem label={'Base price'} value={value} currency={currency} />
      </div>
      {isUKbooking && (
        <div style={{ paddingLeft: '16px' }}>
          <PriceItem
            label='Includes taxes (VAT 20%)'
            value={value * (20 / 120)}
            currency={currency}
          />
          <PriceItem
            label='Net amount'
            value={value / 1.2}
            currency={currency}
          />
        </div>
      )}
    </>
  )

  const renderExtendedGreeterItem = () => {
    if (!(vipAddlHoursNum > 0)) return null
    const label =
      vipAddlHoursCount > 0
        ? `Extended greeter assistance (${vipAddlHoursCount} hour${
            vipAddlHoursCount === 1 ? '' : 's'
          })`
        : 'Extended greeter assistance'
    const hasDetail =
      vipAddlHoursDiscountNum > 0 ||
      vipAddlHoursCommissionNum > 0 ||
      vipAddlHoursMarkupNum > 0
    // No detail to show: fall back to the shared coverage row so this stays
    // byte-identical for every booking whose hours carry no discount,
    // commission or markup.
    if (!hasDetail) return renderCoverageItem(label, vipAddlHoursNum)
    return (
      <>
        <hr />
        <PriceItem label={label} value={vipAddlHoursNum} currency={currency} />
        <div style={{ paddingLeft: '16px' }}>
          {vipAddlHoursDiscountNum > 0 && (
            <PriceItem
              label='Discount'
              value={vipAddlHoursDiscountNum}
              currency={currency}
              weight={discWeight || null}
              isNegative
            />
          )}
          {isUKbooking && (
            <>
              <PriceItem
                label='Includes taxes (VAT 20%)'
                value={vipAddlHoursNum * (20 / 120)}
                currency={currency}
              />
              <PriceItem
                label='Net amount'
                value={vipAddlHoursNum / 1.2}
                currency={currency}
              />
            </>
          )}
          {vipAddlHoursCommissionNum > 0 && (
            <PriceItem
              label='Your commission'
              value={vipAddlHoursCommissionNum}
              currency={currency}
              weight={commissionWeight || null}
            />
          )}
          {vipAddlHoursMarkupNum > 0 && (
            <>
              <PriceItem
                label='Price markup'
                value={vipAddlHoursMarkupNum}
                currency={currency}
                weight={markupWeight}
              />
              {isUKbooking && (
                <>
                  <PriceItem
                    label='Includes taxes (VAT 20%)'
                    value={vipAddlHoursMarkupNum * (20 / 120)}
                    currency={currency}
                  />
                  <PriceItem
                    label='Net markup'
                    value={vipAddlHoursMarkupNum / 1.2}
                    currency={currency}
                  />
                </>
              )}
            </>
          )}
        </div>
      </>
    )
  }

  const renderChargesSection = () => {
    if (!hasCharges) return null
    const chargeItems = charges.flatMap((charge, chargeIdx) =>
      (charge.items || []).map((item, itemIdx) => ({
        item,
        charge,
        key: `charge-${chargeIdx}-${itemIdx}`,
        isLastItem: itemIdx === charge.items.length - 1,
      }))
    )
    return (
      <>
        <hr />
        {chargeItems.map(({ item, charge, key, isLastItem }, idx) => (
          <React.Fragment key={key}>
            {idx > 0 && <hr />}
            {renderChargeItem(item, charge, key, isLastItem)}
          </React.Fragment>
        ))}
      </>
    )
  }

  const renderCombinedBreakdown = () => {
    const renderSection = ({
      label,
      sectionTotal,
      sectionBase,
      sectionMarkup,
      sectionMarkupWeight,
      sectionCommission,
      sectionDiscount,
    }) => {
      const netBeforeMarkup = sectionTotal - sectionMarkup
      const discountPct =
        sectionDiscount > 0 && sectionBase > 0
          ? Math.round((sectionDiscount / sectionBase) * 100)
          : null
      return (
        <>
          <PriceItem label={label} value={sectionTotal} currency={currency} />
          <div style={{ paddingLeft: '16px' }}>
            {sectionBase !== sectionTotal && (
              <PriceItem
                label='Base price'
                value={sectionBase}
                currency={currency}
              />
            )}
            {sectionDiscount > 0 && (
              <PriceItem
                label='Discount'
                value={sectionDiscount}
                currency={currency}
                weight={discountPct}
                isNegative
              />
            )}
            {isUKbooking && netBeforeMarkup > 0 && (
              <>
                <PriceItem
                  label='Includes taxes (VAT 20%)'
                  value={netBeforeMarkup * (20 / 120)}
                  currency={currency}
                />
                <PriceItem
                  label='Net amount'
                  value={netBeforeMarkup / 1.2}
                  currency={currency}
                />
              </>
            )}
            {sectionCommission > 0 && (
              <PriceItem
                label='Your commission'
                value={sectionCommission}
                currency={currency}
                weight={commissionWeight > 0 ? commissionWeight : null}
              />
            )}
            {sectionMarkup > 0 && (
              <>
                <PriceItem
                  label='Price markup'
                  value={sectionMarkup}
                  currency={currency}
                  weight={sectionMarkupWeight}
                />
                {isUKbooking && (
                  <>
                    <PriceItem
                      label='Includes taxes (VAT 20%)'
                      value={sectionMarkup * (20 / 120)}
                      currency={currency}
                    />
                    <PriceItem
                      label='Net markup'
                      value={sectionMarkup / 1.2}
                      currency={currency}
                    />
                  </>
                )}
              </>
            )}
          </div>
        </>
      )
    }

    const buildVipSection = (leg, legLabel) => {
      // A leg a discount brought to 0 still shows, with its base price and discount
      if (!leg || (!leg.rate && !(parseFloat(leg.baseRate) > 0))) return null
      const legTotal = parseFloat(leg.rate || 0)
      const legMarkupAmt = parseFloat(leg.clientMarkupRate || 0)
      const legBase =
        leg.baseRate != null
          ? parseFloat(leg.baseRate)
          : Math.max(0, legTotal - legMarkupAmt)
      const legMarkupConfig = leg.clientMarkup
      const legMarkupW =
        legMarkupConfig?.type === 'percentage' && legMarkupConfig?.value > 0
          ? legMarkupConfig.value
          : null
      const legCommission = calculateCommissionValue(
        legBase,
        commissionWeight || 0,
        discountWeightValue,
        (pickUpLocation || booking.pickUpLocation)?.country
      )
      const legDiscount = Math.max(0, legBase - legMarkupAmt - legTotal)
      return {
        label: legLabel,
        sectionTotal: legTotal,
        sectionBase: legBase,
        sectionMarkup: legMarkupAmt,
        sectionMarkupWeight: legMarkupW,
        sectionCommission: legCommission,
        sectionDiscount: legDiscount,
      }
    }

    const transferBase = Math.max(
      0,
      totalRate -
        _baseServiceMarkup +
        totalDiscountValue +
        referralNum +
        creditsNum
    )
    const resolvedTransferBase = transferBase > 0 ? transferBase : totalRate
    const transferDiscount = Math.max(
      0,
      resolvedTransferBase - _baseServiceMarkup - totalRate
    )

    const transferSection = {
      label: serviceLabel,
      sectionTotal: totalRate,
      sectionBase: resolvedTransferBase,
      sectionMarkup: _baseServiceMarkup,
      sectionMarkupWeight: resolvedMarkupWeight,
      sectionCommission: baseCommissionValue,
      sectionDiscount: transferDiscount,
    }

    const arrivalSection = buildVipSection(arrival, 'VIP Meet & Greet, Arrival')
    const departureSection = buildVipSection(
      departure,
      'VIP Meet & Greet, Departure'
    )

    return (
      <>
        <p className='title-text'>Price breakdown</p>
        {(totalRate > 0 || totalDiscountValue > 0) &&
          renderSection(transferSection)}
        {renderChargesSection()}
        {arrivalSection && (
          <>
            <hr />
            {renderSection(arrivalSection)}
          </>
        )}
        {departureSection && (
          <>
            <hr />
            {renderSection(departureSection)}
          </>
        )}
        {renderExtendedGreeterItem()}
        {waitCoverageNum > 0 &&
          !waitChargeItem &&
          renderCoverageItem('Extended waiting time', waitCoverageNum)}
        {cancelCoverageNum > 0 &&
          !cancelChargeItem &&
          renderCoverageItem(
            '1-hour risk-free cancellation',
            cancelCoverageNum
          )}
        {renderTotalPriceLine()}
      </>
    )
  }

  // One priced instance (the VIP service, or the purchased hours): its charged
  // total, then Base price (only when a discount or markup moves it), Discount,
  // UK VAT + Net amount on the pre-markup price, commission, and the markup
  // with its own UK VAT + Net markup.
  const renderVipInstance = ({
    label,
    total,
    base,
    discount,
    commission,
    markup,
  }) => {
    const preMarkup = Math.max(0, base - discount)
    return (
      <>
        <PriceItem label={label} value={total} currency={currency} />
        <div style={{ paddingLeft: '16px' }}>
          {(discount > 0 || markup > 0) && (
            <PriceItem label='Base price' value={base} currency={currency} />
          )}
          {discount > 0 && (
            <PriceItem
              label={isB2C ? 'Sign up discount applied' : 'Discount'}
              value={discount}
              currency={currency}
              weight={!isB2C ? discWeight : null}
              isNegative
            />
          )}
          {isUKbooking && preMarkup > 0 && (
            <>
              <PriceItem
                label='Includes taxes (VAT 20%)'
                value={preMarkup * (20 / 120)}
                currency={currency}
              />
              <PriceItem
                label='Net amount'
                value={preMarkup / 1.2}
                currency={currency}
              />
            </>
          )}
          {commission > 0 && (
            <PriceItem
              label='Your commission'
              value={commission}
              currency={currency}
              weight={vipCommissionWeight > 0 ? vipCommissionWeight : null}
            />
          )}
          {markup > 0 && (
            <>
              <PriceItem
                label='Price markup'
                value={markup}
                currency={currency}
                weight={resolvedMarkupWeight}
              />
              {isUKbooking && (
                <>
                  <PriceItem
                    label='Includes taxes (VAT 20%)'
                    value={markup * (20 / 120)}
                    currency={currency}
                  />
                  <PriceItem
                    label='Net markup'
                    value={markup / 1.2}
                    currency={currency}
                  />
                </>
              )}
            </>
          )}
        </div>
      </>
    )
  }

  const renderVipHoursBreakdown = () => {
    // The hours only exist on a Transit booking; name it like the Figma does
    // unless the caller's label already says so.
    const vipLabel = /transit/i.test(serviceLabel)
      ? serviceLabel
      : `${serviceLabel}, Transit`
    const hoursLabel =
      vipAddlHoursCount > 0
        ? `Extended greeter assistance (${vipAddlHoursCount} hour${
            vipAddlHoursCount === 1 ? '' : 's'
          })`
        : 'Extended greeter assistance'
    return (
      <>
        <p className='title-text'>Price breakdown</p>
        {renderVipInstance({
          label: vipLabel,
          total: vipOwnTotal,
          base: vipOwnBase,
          discount: vipOwnDiscount,
          commission: vipOwnCommission,
          markup: vipOwnMarkup,
        })}
        <hr />
        {renderVipInstance({
          label: hoursLabel,
          total: vipHoursTotal,
          base: vipAddlHoursNum,
          discount: vipAddlHoursDiscountNum,
          commission: vipAddlHoursCommissionNum,
          markup: vipHoursMarkupNum,
        })}
        {renderChargesSection()}
        {referralNum > 0 && (
          <>
            <hr />
            <PriceItem
              label='Referral discount applied'
              value={referralNum}
              currency={currency}
              isNegative
            />
          </>
        )}
        {creditsNum > 0 && (
          <>
            <hr />
            <PriceItem
              label='ROLZO credits redeemed'
              value={creditsNum}
              currency={currency}
              isNegative
            />
          </>
        )}
        <hr />
        <PriceItem
          label={<b>Total price</b>}
          value={grandTotalPrice}
          currency={currency}
          bold
        />
      </>
    )
  }

  const renderPriceBreakdown = () => {
    if (priveDiscountApplied) {
      return (
        <>
          <p className='title-text'>Price breakdown</p>
          <div className='price-item'>
            <span className='label'>ROLZO Privé discount applied</span>
            <span className='dots'></span>
            <span className='value'>-20%</span>
          </div>
        </>
      )
    }

    if (referralNum > 0 && totalRate === 0) {
      return (
        <>
          <p className='title-text'>Price breakdown</p>
          <PriceItem
            label='Referral discount applied'
            value={referralNum}
            currency={currency}
            isNegative
          />
        </>
      )
    }

    if (isB2C && !hasBenefits && totalDiscountValue > 0) {
      return (
        <>
          <p className='title-text'>Price breakdown</p>
          <PriceItem
            label='Sign up discount applied'
            value={totalDiscountValue}
            currency={currency}
            isNegative
          />
        </>
      )
    }

    if ((arrival || departure) && (totalRate > 0 || totalDiscountValue > 0)) {
      return renderCombinedBreakdown()
    }

    if (isVipHoursLayout) {
      return renderVipHoursBreakdown()
    }

    return (
      <>
        <p className='title-text'>Price breakdown</p>
        {totalPrice > 0 && (
          <PriceItem
            label={serviceLabel}
            value={totalPrice}
            currency={currency}
          />
        )}
        <div style={{ paddingLeft: '16px' }}>
          {(totalRate > 0 || baseServiceRate > 0) && (
            <>
              {baseServiceRateExHours !== totalPrice && (
                <PriceItem
                  label='Base price'
                  value={
                    baseServiceRate > 0
                      ? baseServiceRateExHours
                      : Math.max(0, totalRate - vipAddlHoursNum)
                  }
                  currency={currency}
                />
              )}
              {baseServiceDiscountExHours > 0 && (
                <PriceItem
                  label={isB2C ? 'Sign up discount applied' : 'Discount'}
                  value={baseServiceDiscountExHours}
                  currency={currency}
                  weight={!isB2C ? discWeight : null}
                  isNegative
                />
              )}
              {isUKbooking && (
                <>
                  <PriceItem
                    label='Includes taxes (VAT 20%)'
                    value={
                      baseServiceRate > 0
                        ? (baseServiceRateExHours -
                            baseServiceDiscountExHours) *
                          (20 / 120)
                        : Math.max(0, totalRate - vipAddlHoursNum) * (20 / 120)
                    }
                    currency={currency}
                  />
                  <PriceItem
                    label='Net amount'
                    value={
                      baseServiceRate > 0
                        ? (baseServiceRateExHours -
                            baseServiceDiscountExHours) /
                          1.2
                        : Math.max(0, totalRate - vipAddlHoursNum) / 1.2
                    }
                    currency={currency}
                  />
                </>
              )}
            </>
          )}
          {baseCommissionExHours > 0 && (
            <PriceItem
              label='Your commission'
              value={baseCommissionExHours}
              currency={currency}
              weight={commissionWeight > 0 ? commissionWeight : null}
            />
          )}
          {baseMarkupExHours > 0 && (
            <>
              <PriceItem
                label='Markup'
                value={baseMarkupExHours}
                currency={currency}
                weight={resolvedMarkupWeight}
              />
              {isUKbooking && (
                <>
                  <PriceItem
                    label='Includes taxes (VAT 20%)'
                    value={baseMarkupExHours * (20 / 120)}
                    currency={currency}
                  />
                  <PriceItem
                    label='Net markup'
                    value={baseMarkupExHours / 1.2}
                    currency={currency}
                  />
                </>
              )}
            </>
          )}
        </div>
        {renderChargesSection()}
        {renderExtendedGreeterItem()}
        {waitCoverageNum > 0 &&
          !waitChargeItem &&
          renderCoverageItem('Extended waiting time', waitCoverageNum)}
        {cancelCoverageNum > 0 &&
          !cancelChargeItem &&
          renderCoverageItem(
            '1-hour risk-free cancellation',
            cancelCoverageNum
          )}
        {referralNum > 0 && (
          <>
            <hr />
            <PriceItem
              label='Referral discount applied'
              value={referralNum}
              currency={currency}
              isNegative
            />
          </>
        )}
        {creditsNum > 0 && (
          <>
            <hr />
            <PriceItem
              label='ROLZO credits redeemed'
              value={creditsNum}
              currency={currency}
              isNegative
            />
          </>
        )}
        {renderTotalPriceLine()}
      </>
    )
  }

  return (
    <Modal
      size='lg'
      show={show}
      onHide={onClose}
      className='client-commission-sales-tax-modal modal-90w'
      dialogClassName='modal-90w '
      aria-labelledby='contained-modal-title-vcenter'
      centered
    >
      <Modal.Header>
        <Modal.Title id='example-modal-sizes-title-lg' className='modal-title'>
          <div className='close-btn-wrap'>
            <Button
              variant='link'
              onClick={onClose}
              className={isMobileBrowser ? 'btn-link-draft-mobile' : 'pr-3'}
            >
              <img
                className={isMobileBrowser ? '' : 'w-100 h-100'}
                src={`${
                  isMobileBrowser
                    ? '/images/icons/close-icon-black.png'
                    : '/images/icons/modal-close-icon.svg'
                }`}
                alt=''
                height={isMobileBrowser ? 12 : 50}
                width={isMobileBrowser ? 12 : 50}
                style={
                  isMobileBrowser
                    ? { marginRight: '12px', marginTop: '10px' }
                    : {}
                }
              />
            </Button>
          </div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {renderPriceBreakdown()}
        {showEarningsSection && (
          <>
            <hr />
            <p className='title-text' style={{ marginTop: '28px' }}>
              Your earnings
            </p>
            {totalCommissionValue > 0 && (
              <PriceItem
                label='Total commission earned'
                value={totalCommissionValue}
                currency={currency}
                weight={
                  vipBreakdown
                    ? vipBreakdown.commissionWeightLabel
                    : commissionWeight
                }
              />
            )}
            {totalMarkupValue > 0 && (
              <PriceItem
                label='Total markup earned'
                value={isUKbooking ? totalMarkupValue / 1.2 : totalMarkupValue}
                currency={currency}
                weight={resolvedMarkupWeight}
              />
            )}
            {totalMarkupValue > 0 && totalCommissionValue > 0 && (
              <PriceItem
                label={
                  isVipHoursLayout ? (
                    <b>Total payout</b>
                  ) : (
                    <>
                      Total <b>payout</b>
                    </>
                  )
                }
                value={totalEarnings}
                currency={currency}
                bold={isVipHoursLayout}
              />
            )}
          </>
        )}
      </Modal.Body>
    </Modal>
  )
}
